import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, Loader2, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { adminAPI } from '@/services/api';
import { StatusBadge, formatDateTime } from './adminUtils';

const STATUSES = ['all', 'unread', 'read', 'replied'];

export default function RequestsPanel() {
  const { token } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ unread: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [reply, setReply] = useState('');
  const [editStatus, setEditStatus] = useState('unread');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = status !== 'all' ? { status } : {};
      const [list, countData] = await Promise.all([
        adminAPI.listMessages(token, params),
        adminAPI.getMessageCount(token),
      ]);
      setItems(list);
      setCounts(countData);
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [token, status]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase().trim();
    return items.filter((m) => {
      const hay = [m.name, m.email, m.phone, m.subject, m.message, m.id]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  const selected = items.find((m) => m.id === selectedId);

  useEffect(() => {
    if (!selected) return;
    setNotes(selected.admin_notes || '');
    setReply(selected.admin_reply || '');
    setEditStatus(selected.status || 'unread');

    if (selected.status === 'unread') {
      adminAPI
        .updateMessage(token, selected.id, { status: 'read' })
        .then((updated) => {
          setItems((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
          setEditStatus('read');
          setCounts((c) => ({
            ...c,
            unread: Math.max(0, (c.unread || 0) - 1),
          }));
        })
        .catch(() => {});
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async ({ sendEmail = false } = {}) => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await adminAPI.updateMessage(token, selected.id, {
        status: sendEmail ? 'replied' : editStatus,
        admin_notes: notes,
        admin_reply: reply,
        send_reply_email: sendEmail,
      });
      setItems((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      setEditStatus(updated.status);
      const countData = await adminAPI.getMessageCount(token);
      setCounts(countData);
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to update message');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Requests / Messages</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {counts.unread || 0} unread · {counts.count || 0} total
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, subject…"
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{s === 'all' ? 'All statuses' : s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid xl:grid-cols-5 gap-4">
        <div className="xl:col-span-2 bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          {loading && items.length === 0 ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-brand-blue-500" size={28} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <Inbox className="mx-auto text-gray-300 mb-3" size={36} />
              <p className="text-sm text-gray-500">No messages yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-50 max-h-[70vh] overflow-y-auto">
              {filtered.map((m) => {
                const active = m.id === selectedId;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(m.id)}
                      className={`w-full text-left px-4 py-3.5 transition-colors ${
                        active ? 'bg-brand-blue-50/70' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm truncate ${m.status === 'unread' ? 'font-bold text-gray-900' : 'font-medium text-gray-800'}`}>
                          {m.name}
                        </p>
                        <StatusBadge status={m.status} />
                      </div>
                      <p className="text-sm text-gray-600 truncate mt-0.5">{m.subject}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatDateTime(m.created_at)}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="xl:col-span-3 bg-white border border-gray-100 rounded-xl shadow-sm p-5">
          {!selected ? (
            <div className="h-full min-h-[280px] flex items-center justify-center text-sm text-gray-400">
              Select a message to read and reply
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{selected.subject}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {selected.name} · {selected.email}
                  {selected.phone ? ` · ${selected.phone}` : ''}
                </p>
                <p className="text-xs text-gray-400 mt-1">{formatDateTime(selected.created_at)}</p>
              </div>

              <div className="rounded-lg bg-gray-50 border border-gray-100 p-4 text-sm text-gray-700 whitespace-pre-wrap">
                {selected.message}
              </div>

              <div>
                <Label>Status</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.filter((s) => s !== 'all').map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="msg-notes">Internal notes</Label>
                <Textarea
                  id="msg-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="mt-1.5"
                  placeholder="Private staff notes…"
                />
              </div>

              <div>
                <Label htmlFor="msg-reply">Reply to customer</Label>
                <Textarea
                  id="msg-reply"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={5}
                  className="mt-1.5"
                  placeholder="Write a reply to email the customer…"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => handleSave({ sendEmail: false })}
                  disabled={saving}
                  className="bg-brand-blue-500 hover:bg-brand-blue-600 text-white"
                >
                  {saving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : null}
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSave({ sendEmail: true })}
                  disabled={saving || !reply.trim()}
                  className="border-brand-gold-300 text-brand-gold-800 hover:bg-brand-gold-50"
                >
                  Save & email reply
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
